import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { GitBranch, Plus, Search, RefreshCw, AlertTriangle, Zap, Clock } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || (window.location.origin + '/api');

// ── Estilos por tipo ──────────────────────────────────────────────────────────
const TYPE_STYLE = {
  normal:    { label: 'Normal',    bg: 'var(--accent-dim)', color: 'var(--accent)', border: 'var(--accent-dim)' },
  standard:  { label: 'Estándar', bg: 'var(--green-dim)', color: 'var(--green)', border: 'var(--green-dim)' },
  emergency: { label: 'Emergencia', bg: 'var(--red-dim)', color: 'var(--red)', border: 'var(--red-dim)' },
};

const STATUS_STYLE = {
  draft:              { label: 'Borrador',         bg: 'var(--bg-subtle)', color: 'var(--ink-2)', border: 'var(--border)' },
  pending_approval:   { label: 'Pend. aprobación', bg: 'var(--amber-dim)', color: 'var(--amber)', border: 'var(--amber-dim)' },
  approved:           { label: 'Aprobado',         bg: 'var(--green-dim)', color: 'var(--green)', border: 'var(--green-dim)' },
  implementing:       { label: 'Implementando',    bg: 'var(--accent-dim)', color: 'var(--accent)', border: 'var(--accent-dim)' },
  review:             { label: 'En revisión',      bg: 'var(--bg-subtle)', color: 'var(--ink-2)', border: 'var(--border)' },
  closed:             { label: 'Cerrado',          bg: 'var(--bg-subtle)', color: 'var(--ink-2)', border: 'var(--border)' },
  rejected:           { label: 'Rechazado',        bg: 'var(--red-dim)', color: 'var(--red)', border: 'var(--red-dim)' },
  cancelled:          { label: 'Cancelado',        bg: 'var(--bg-subtle)', color: 'var(--ink-3)', border: 'var(--border)' },
};

const PRIORITY_COLOR = {
  critica: 'var(--red)', alta: 'var(--amber)', media: 'var(--accent)', baja: 'var(--ink-2)',
};

function Badge({ style, children }) {
  return (
    <span style={{
      display: 'inline-block', fontSize: 11, fontWeight: 600, lineHeight: 1,
      padding: '3px 8px', borderRadius: 4,
      background: style.bg, color: style.color, border: `1px solid ${style.border}`,
    }}>{children}</span>
  );
}

const TABS = [
  { key: 'all',            label: 'Todos' },
  { key: 'draft',          label: 'Borrador' },
  { key: 'pending_approval', label: 'Pendiente' },
  { key: 'approved',       label: 'Aprobado' },
  { key: 'implementing',   label: 'Implementando' },
  { key: 'review',         label: 'Revisión' },
  { key: 'closed',         label: 'Cerrado' },
];

export default function Changes() {
  const navigate = useNavigate();
  const [changes, setChanges]   = useState([]);
  const [stats, setStats]       = useState({});
  const [loading, setLoading]   = useState(true);
  const [tab, setTab]           = useState('all');
  const [search, setSearch]     = useState('');
  const [typeFilter, setTypeFilter] = useState('');

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (tab !== 'all') params.set('status', tab);
      if (search)        params.set('search', search);
      if (typeFilter)    params.set('type', typeFilter);

      const [cRes, sRes] = await Promise.all([
        fetch(`${API_URL}/changes?${params}`, { headers }),
        fetch(`${API_URL}/changes/stats`, { headers }),
      ]);
      if (cRes.ok) setChanges(await cRes.json());
      if (sRes.ok) setStats(await sRes.json());
    } finally {
      setLoading(false);
    }
  }, [tab, search, typeFilter]);

  useEffect(() => { load(); }, [load]);

  function fmt(dt) {
    if (!dt) return '—';
    return new Date(dt).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  }

  return (
    <div style={{ maxWidth: 1100 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <GitBranch size={22} style={{ opacity: 0.7 }} />
          <div>
            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>Gestión de Cambios</h1>
            <p style={{ margin: 0, fontSize: 12, color: 'var(--ink-2)' }}>Solicitudes de cambio (RFC) — ciclo de vida ITIL</p>
          </div>
        </div>
        <button
          onClick={() => navigate('/changes/new')}
          style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '9px 18px', background: 'var(--ink)', color: 'var(--surface)', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
          <Plus size={15} /> Nuevo RFC
        </button>
      </div>

      {/* Stat strip */}
      {Object.keys(stats).length > 0 && (
        <div style={{ display: 'flex', gap: 0, marginBottom: 24, border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
          {[
            { label: 'Total',        value: stats.total,               color: 'var(--ink)' },
            { label: 'Pendientes',   value: stats.pendiente_aprobacion, color: 'var(--amber)' },
            { label: 'Aprobados',    value: stats.aprobado,            color: 'var(--green)' },
            { label: 'Implementando',value: stats.implementando,       color: 'var(--accent)' },
            { label: 'Emergencias',  value: stats.emergencias_activas, color: 'var(--red)' },
          ].map((s, i, arr) => (
            <div key={s.label} style={{ flex: 1, padding: '14px 16px', textAlign: 'center', borderRight: i < arr.length - 1 ? '1px solid var(--border)' : 'none', background: 'var(--surface)' }}>
              <div style={{ fontSize: 22, fontWeight: 800, color: s.color, lineHeight: 1 }}>{s.value ?? 0}</div>
              <div style={{ fontSize: 11, color: 'var(--ink-2)', marginTop: 3 }}>{s.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Tabs + filtros */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
        <div style={{ display: 'flex', gap: 2, borderBottom: '2px solid var(--border)' }}>
          {TABS.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              style={{ padding: '7px 14px', border: 'none', background: 'none', fontSize: 13, fontWeight: tab === t.key ? 700 : 400, color: tab === t.key ? 'var(--ink)' : 'var(--ink-2)', borderBottom: tab === t.key ? '2px solid var(--ink)' : '2px solid transparent', marginBottom: -2, cursor: 'pointer' }}>
              {t.label}
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <div style={{ position: 'relative' }}>
            <Search size={13} style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', color: 'var(--ink-3)' }} />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar..." style={{ paddingLeft: 28, paddingRight: 10, height: 34, border: '1px solid var(--border)', borderRadius: 7, fontSize: 13, width: 180 }} />
          </div>
          <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)}
            style={{ height: 34, padding: '0 10px', border: '1px solid var(--border)', borderRadius: 7, fontSize: 13, color: 'var(--ink-2)' }}>
            <option value="">Todos los tipos</option>
            <option value="normal">Normal</option>
            <option value="standard">Estándar</option>
            <option value="emergency">Emergencia</option>
          </select>
          <button onClick={load} style={{ height: 34, width: 34, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid var(--border)', borderRadius: 7, background: 'var(--surface)', cursor: 'pointer' }}>
            <RefreshCw size={14} style={{ color: 'var(--ink-2)', animation: loading ? 'spin .8s linear infinite' : 'none' }} />
          </button>
        </div>
      </div>

      {/* Tabla */}
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 48, textAlign: 'center', color: 'var(--ink-3)' }}>
            <RefreshCw size={20} style={{ animation: 'spin .8s linear infinite' }} />
          </div>
        ) : changes.length === 0 ? (
          <div style={{ padding: 48, textAlign: 'center', color: 'var(--ink-3)' }}>
            <GitBranch size={32} style={{ marginBottom: 8, opacity: 0.3 }} />
            <div>No hay cambios registrados</div>
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--bg)', background: 'var(--bg-subtle)' }}>
                {['Número', 'Título', 'Tipo', 'Estado', 'Prioridad', 'Asignado a', 'Planificado', ''].map(h => (
                  <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: 'var(--ink-2)', fontSize: 12 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {changes.map((c, i) => {
                const ts = STATUS_STYLE[c.status] || STATUS_STYLE.draft;
                const tt = TYPE_STYLE[c.change_type] || TYPE_STYLE.normal;
                return (
                  <tr key={c.id} onClick={() => navigate(`/changes/${c.id}`)}
                    style={{ borderBottom: i < changes.length - 1 ? '1px solid var(--bg)' : 'none', cursor: 'pointer', transition: 'background .1s' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-subtle)'}
                    onMouseLeave={e => e.currentTarget.style.background = ''}>
                    <td style={{ padding: '12px 14px', fontWeight: 700, color: 'var(--ink-2)', whiteSpace: 'nowrap' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        {c.change_type === 'emergency' && <Zap size={12} style={{ color: 'var(--red)' }} />}
                        {c.change_number}
                      </div>
                    </td>
                    <td style={{ padding: '12px 14px', maxWidth: 280 }}>
                      <div style={{ fontWeight: 500, color: 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.title}</div>
                    </td>
                    <td style={{ padding: '12px 14px', whiteSpace: 'nowrap' }}>
                      <Badge style={tt}>{tt.label}</Badge>
                    </td>
                    <td style={{ padding: '12px 14px', whiteSpace: 'nowrap' }}>
                      <Badge style={ts}>{ts.label}</Badge>
                    </td>
                    <td style={{ padding: '12px 14px', whiteSpace: 'nowrap' }}>
                      <span style={{ fontWeight: 600, color: PRIORITY_COLOR[c.priority] || 'var(--ink-2)', fontSize: 12, textTransform: 'capitalize' }}>{c.priority}</span>
                    </td>
                    <td style={{ padding: '12px 14px', color: 'var(--ink-2)' }}>{c.assigned_to_name || '—'}</td>
                    <td style={{ padding: '12px 14px', color: 'var(--ink-2)', whiteSpace: 'nowrap' }}>
                      {c.planned_start ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <Clock size={11} />
                          {fmt(c.planned_start)}
                        </div>
                      ) : '—'}
                    </td>
                    <td style={{ padding: '12px 14px' }}>
                      <span style={{ fontSize: 16, color: 'var(--ink-3)' }}>›</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
